import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useNotify } from "react-admin";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PaymentIcon from "@material-ui/icons/Payment";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import ErrorIcon from "@material-ui/icons/Error";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

const ARCHIVABLE_STATUSES = ["ordered", "activated", "swept"];

const isCancellable = (card: any): boolean => !!card && card.status === "ordered";

const isSweepable = (card: any): boolean => !!card && card.status === "ordered";

// Reward code is what identifies the physical card with NeoCurrency; it is null until ordered.
const cardRef = (card: any): string =>
  card?.reward_code || card?.reference_id || `#${card?.id}`;

// Matches SWEEP_THRESHOLD on the sweep service.
const SWEEP_MAX = 1;

const EVENT_ICONS: Record<string, React.ReactElement> = {
  payment_initiated: <PaymentIcon style={{ color: "#1565c0" }} />,
  payment_confirmed: <CheckCircleIcon style={{ color: "#2e7d32" }} />,
  card_ordered: <CreditCardIcon style={{ color: "#2e7d32" }} />,
  cashback_credited: <AccountBalanceWalletIcon style={{ color: "#6a1b9a" }} />,
  balance_checked: <VisibilityIcon style={{ color: "#757575" }} />,
  sweep_completed: <SwapHorizIcon style={{ color: "#00695c" }} />,
  payment_failed: <ErrorIcon style={{ color: "#c62828" }} />,
  card_order_failed: <ErrorIcon style={{ color: "#c62828" }} />,
};

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "1em",
  },
  section: {
    width: "100%",
    maxWidth: 1200,
    margin: "0.5em auto",
  },
  paper: {
    padding: "1.5em",
    marginBottom: "1em",
  },
  badgeRow: {
    display: "flex",
    gap: "0.5em",
    flexWrap: "wrap" as any,
    marginTop: "0.5em",
  },
  kvRow: {
    padding: "4px 0",
  },
  kvLabel: {
    fontWeight: 600,
    color: "#666",
    fontSize: "0.85rem",
  },
  kvValue: {
    fontSize: "0.9rem",
  },
  tableCell: {
    padding: "6px 10px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
  },
  tableCellHead: {
    padding: "6px 10px",
    border: "1px solid #ddd",
    fontWeight: 700,
    fontSize: "0.85rem",
    backgroundColor: "#f5f5f5",
  },
  metadataPre: {
    fontSize: "0.75rem",
    backgroundColor: "#f5f5f5",
    padding: "0.5em",
    borderRadius: 4,
    maxWidth: 400,
    overflow: "auto",
  },
}));

interface MastercardAnywhereDetailProps {
  // When rendered inside a Dialog from the dashboard, the cardId is passed
  // as a prop; the URL has no /:id segment. When rendered as a standalone
  // page (deep link from Slack etc.), the route param drives it.
  cardIdProp?: number;
  // Hides the "Back to Dashboard" button (Dialog has its own close).
  embedded?: boolean;
  // Called after a successful archive/cancel so the dashboard can refresh.
  onActionComplete?: () => void;
}

const MastercardAnywhereDetail = ({ cardIdProp, embedded, onActionComplete }: MastercardAnywhereDetailProps = {}) => {
  const classes = useStyles();
  const params = useParams<{ id?: string }>();
  const id = cardIdProp != null ? String(cardIdProp) : params.id;
  const history = useHistory();
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState<any>(null);
  const [audit, setAudit] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [lifecycle, setLifecycle] = useState<any>({});
  const [expandedMeta, setExpandedMeta] = useState<number | null>(null);

  // Archive / Cancel actions
  const [confirmAction, setConfirmAction] = useState<"archive" | "cancel" | "sweep" | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [refundFunds, setRefundFunds] = useState(true);
  const [refundAmount, setRefundAmount] = useState("");
  const [sweepAmount, setSweepAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res: any = await axios.get(`${ENDPOINTS.MC_ANYWHERE_ADMIN}/${id}`, getConfigGet({}));
      if (res.data?.status) {
        setCard(res.data.data.card);
        setAudit(res.data.data.audit || []);
        setRewards(res.data.data.rewards || []);
        setLifecycle(res.data.data.lifecycle_summary || {});
      } else {
        notify("Card not found", "warning");
      }
    } catch (err) {
      notify("Failed to load card", "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleArchive = async () => {
    if (!card) return;
    setSubmitting(true);
    try {
      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ADMIN,
        { action: "archive", card_id: card.id },
        { headers: getConfigPost() }
      );
      if (res.data?.status) {
        notify(res.data.message || "Card archived", "info");
        setConfirmAction(null);
        await fetchDetail();
        onActionComplete?.();
      } else {
        notify(res.data?.message || "Failed to archive card", "warning");
      }
    } catch (err: any) {
      notify(err?.response?.data?.message || "Failed to archive card", "warning");
    } finally {
      setSubmitting(false);
    }
  };

  const sweepAmountError = (): string | null => {
    const value = parseFloat(sweepAmount);
    if (!sweepAmount || Number.isNaN(value) || value <= 0) return "Enter an amount greater than 0";
    if (value >= SWEEP_MAX) return `Must be less than £${SWEEP_MAX.toFixed(2)}`;
    return null;
  };

  const handleSweep = async () => {
    if (!card || sweepAmountError()) return;
    setSubmitting(true);
    try {
      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ADMIN,
        { action: "sweep", card_id: card.id, amount: parseFloat(sweepAmount) },
        { headers: getConfigPost() }
      );
      if (res.data?.status) {
        notify(res.data.message || "Card swept", "info");
        setConfirmAction(null);
        setSweepAmount("");
        await fetchDetail();
        onActionComplete?.();
      } else {
        notify(res.data?.message || "Failed to sweep card", "warning");
      }
    } catch (err: any) {
      notify(err?.response?.data?.message || "Failed to sweep card", "warning");
    } finally {
      setSubmitting(false);
    }
  };

  const openCancelDialog = () => {
    setRefundFunds(!card?.is_refunded);
    setRefundAmount(parseFloat(card?.denomination || 0).toFixed(2));
    setConfirmAction("cancel");
  };

  const closeCancelDialog = () => {
    setConfirmAction(null);
    setCancelReason("");
  };

  const refundAmountError = (): string | null => {
    if (!refundFunds) return null;
    const value = parseFloat(refundAmount);
    const max = parseFloat(card?.denomination || 0);
    if (!refundAmount || Number.isNaN(value) || value <= 0) return "Enter an amount greater than 0";
    if (value > max) return `Cannot exceed the £${max.toFixed(2)} paid`;
    return null;
  };

  const handleCancel = async () => {
    if (!card) return;
    if (refundAmountError()) return;
    setSubmitting(true);
    try {
      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ADMIN,
        {
          action: "cancel",
          card_id: card.id,
          reason: cancelReason || undefined,
          refund_funds: refundFunds,
          refund_amount: refundFunds ? parseFloat(refundAmount) : undefined,
        },
        { headers: getConfigPost() }
      );
      if (res.data?.status) {
        const clawback = res.data.data?.reward_clawback;
        const clawbackNote = clawback?.cancelled
          ? ` (£${Number(clawback.amount).toFixed(2)} cashback reversed)`
          : clawback?.reason
          ? ` (reward: ${clawback.reason})`
          : "";
        const refund = res.data.data?.funds_refund;
        // refund_type 'manual' means no automatic rail worked — money has NOT moved.
        const manualRefund = refund?.refunded && refund?.refund_type === "manual";
        const refundNote = !refundFunds
          ? ""
          : manualRefund
          ? ` — MANUAL REFUND NEEDED: £${Number(refund.amount).toFixed(2)} could not be sent automatically`
          : refund?.refunded
          ? ` — £${Number(refund.amount).toFixed(2)} refunded via ${refund.refund_type || "payment service"}`
          : ` — REFUND FAILED: ${refund?.reason || "unknown"}`;
        notify(
          (res.data.message || "Card cancelled") + clawbackNote + refundNote,
          refundFunds && (!refund?.refunded || manualRefund) ? "warning" : "info"
        );
        closeCancelDialog();
        await fetchDetail();
        onActionComplete?.();
      } else {
        notify(res.data?.message || "Failed to cancel card", "warning");
      }
    } catch (err: any) {
      notify(err?.response?.data?.message || "Failed to cancel card", "warning");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.main}>
        <Box py={4}><CircularProgress /></Box>
      </div>
    );
  }

  if (!card) {
    return (
      <div className={classes.main}>
        <Typography color="error">Card not found</Typography>
      </div>
    );
  }

  const KV = ({ label, value }: { label: string; value: any }) => (
    <Grid item xs={6} className={classes.kvRow}>
      <Typography className={classes.kvLabel}>{label}</Typography>
      <Typography className={classes.kvValue}>{value ?? "-"}</Typography>
    </Grid>
  );

  const lifecycleSteps = [
    { key: "payment_initiated", label: "Payment Initiated" },
    { key: "payment_confirmed", label: "Payment Confirmed" },
    { key: "card_ordered", label: "Card Ordered" },
    { key: "cashback_credited", label: "Cashback Credited" },
    { key: "swept", label: "Swept" },
  ];

  return (
    <div className={classes.main}>
      <div className={classes.section}>
        {/* Back — hidden when embedded in a Dialog (which provides its own close) */}
        {!embedded && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/mastercardAnywhere")} style={{ marginBottom: "1em" }}>
            Back to Dashboard
          </Button>
        )}

        {/* Card Info */}
        <Paper className={classes.paper}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb="0.5em">
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Card #{card.id}
              <Chip
                label={card.status}
                size="small"
                style={{ marginLeft: "1em", fontWeight: 600 }}
              />
            </Typography>
            <Box display="flex" style={{ gap: 8 }}>
              {ARCHIVABLE_STATUSES.includes(card.status) && (
                <Button size="small" variant="outlined" onClick={() => setConfirmAction("archive")}>
                  Archive
                </Button>
              )}
              {isSweepable(card) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setSweepAmount(""); setConfirmAction("sweep"); }}
                >
                  Sweep
                </Button>
              )}
              {isCancellable(card) && (
                <Button
                  size="small"
                  variant="outlined"
                  style={{ color: "#c62828", borderColor: "#c62828" }}
                  onClick={openCancelDialog}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
          <Grid container spacing={1}>
            <KV label="User" value={`${card.first_name} ${card.last_name} (${card.email})`} />
            <KV label="User ID" value={card.user_id} />
            <KV label="Denomination" value={`£${parseFloat(card.denomination).toFixed(2)}`} />
            <KV label="Cashback" value={`£${parseFloat(card.cashback_amount || 0).toFixed(2)} (${(parseFloat(card.cashback_rate || 0) * 100).toFixed(2)}%)`} />
            <KV label="Cashback Credited" value={card.cashback_credited ? "Yes" : "No"} />
            <KV label="Sweep Amount" value={card.sweep_amount ? `£${parseFloat(card.sweep_amount).toFixed(2)}` : "-"} />
            <KV label="Brand ID" value={card.brand_id} />
            <KV label="Payment Source" value={card.payment_source} />
            <KV label="Payment ID" value={card.payment_id} />
            <KV label="NeoCurrency Order" value={card.neocurrency_order_id} />
            <KV
              label="Reward Code"
              value={
                card.reward_code ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, wordBreak: "break-all" }}>
                    {card.reward_code}
                    <IconButton
                      size="small"
                      title="Copy reward code"
                      onClick={() => {
                        navigator.clipboard.writeText(card.reward_code);
                        notify("Reward code copied", "info");
                      }}
                    >
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                    {card.activation_url && (
                      <IconButton
                        size="small"
                        title="Open in NeoCurrency portal"
                        component="a"
                        href={card.activation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </span>
                ) : (
                  "-"
                )
              }
            />
            <KV label="Expires" value={card.expires_at ? card.expires_at.slice(0, 10).split("-").reverse().join("/") : "-"} />
            <KV label="Created" value={card.createdAt?.slice(0, 19).replace("T", " ")} />
            <KV label="Updated" value={card.updatedAt?.slice(0, 19).replace("T", " ")} />
            {card.error_message && <KV label="Error" value={<Typography color="error" variant="body2">{card.error_message}</Typography>} />}
          </Grid>
        </Paper>

        {/* Lifecycle Summary */}
        <Paper className={classes.paper}>
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: "0.5em" }}>Lifecycle</Typography>
          <div className={classes.badgeRow}>
            {lifecycleSteps.map((s) => (
              <Chip
                key={s.key}
                label={s.label}
                size="small"
                style={{
                  backgroundColor: lifecycle[s.key] ? "#e8f5e9" : "#f5f5f5",
                  color: lifecycle[s.key] ? "#2e7d32" : "#999",
                  fontWeight: 600,
                }}
                icon={lifecycle[s.key] ? <CheckCircleIcon style={{ color: "#2e7d32", fontSize: 16 }} /> : undefined}
              />
            ))}
            {lifecycle.has_errors && (
              <Chip label="Has Errors" size="small" style={{ backgroundColor: "#fce4ec", color: "#c62828", fontWeight: 600 }} icon={<ErrorIcon style={{ color: "#c62828", fontSize: 16 }} />} />
            )}
            <Chip
              label={`Total to user: £${parseFloat(lifecycle.total_value_to_user || 0).toFixed(2)}`}
              size="small"
              style={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
            />
          </div>
        </Paper>

        {/* Audit Timeline */}
        <Paper className={classes.paper}>
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: "0.5em" }}>Audit Trail</Typography>
          {audit.length === 0 ? (
            <Typography color="textSecondary">No audit events recorded</Typography>
          ) : (
            <Stepper orientation="vertical" nonLinear>
              {audit.map((event: any, i: number) => (
                <Step key={i} active completed>
                  <StepLabel
                    icon={EVENT_ICONS[event.event] || <CheckCircleIcon style={{ color: "#999" }} />}
                  >
                    <strong>{event.event}</strong>
                    <Typography variant="caption" style={{ marginLeft: "1em", color: "#999" }}>
                      {event.created_at?.slice(0, 19).replace("T", " ")}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    {event.previous_status && (
                      <Typography variant="body2">
                        Status: {event.previous_status} &rarr; {event.new_status}
                      </Typography>
                    )}
                    {event.new_status && !event.previous_status && (
                      <Typography variant="body2">Status: &rarr; {event.new_status}</Typography>
                    )}
                    {event.amount && (
                      <Typography variant="body2">Amount: £{parseFloat(event.amount).toFixed(2)}</Typography>
                    )}
                    {event.neocurrency_order_id && (
                      <Typography variant="body2">Order ID: {event.neocurrency_order_id}</Typography>
                    )}
                    {event.payment_id && (
                      <Typography variant="body2">Payment: {event.payment_id}</Typography>
                    )}
                    {event.error_message && (
                      <Typography variant="body2" color="error">{event.error_message}</Typography>
                    )}
                    {event.metadata && (
                      <>
                        <IconButton size="small" onClick={() => setExpandedMeta(expandedMeta === i ? null : i)}>
                          <ExpandMoreIcon style={{ transform: expandedMeta === i ? "rotate(180deg)" : "none", transition: "0.2s" }} />
                        </IconButton>
                        <Collapse in={expandedMeta === i}>
                          <pre className={classes.metadataPre}>
                            {JSON.stringify(typeof event.metadata === "string" ? JSON.parse(event.metadata) : event.metadata, null, 2)}
                          </pre>
                        </Collapse>
                      </>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          )}
        </Paper>

        {/* Reward Records */}
        <Paper className={classes.paper}>
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: "0.5em" }}>Reward Records</Typography>
          {rewards.length === 0 ? (
            <Typography color="textSecondary">No reward records</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["ID", "Source", "Amount", "Inflight", "Status", "Created"].map((h) => (
                    <TableCell key={h} className={classes.tableCellHead}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rewards.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className={classes.tableCell}>{r.id}</TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label={r.source_type === "mastercard_anywhere_cashback" ? "Cashback" : "Sweep"}
                        size="small"
                        style={{
                          backgroundColor: r.source_type === "mastercard_anywhere_cashback" ? "#e3f2fd" : "#e8f5e9",
                          color: r.source_type === "mastercard_anywhere_cashback" ? "#1565c0" : "#2e7d32",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>£{parseFloat(r.amount).toFixed(2)}</TableCell>
                    <TableCell className={classes.tableCell}>{r.is_inflight ? "Yes" : "No"}</TableCell>
                    <TableCell className={classes.tableCell}>{r.transaction_status}</TableCell>
                    <TableCell className={classes.tableCell}>{r.createdAt?.slice(0, 19).replace("T", " ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </div>

      <Dialog open={confirmAction === "archive"} onClose={() => !submitting && setConfirmAction(null)}>
        <DialogTitle>Archive card?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This hides card <strong>{cardRef(card)}</strong> ({card.email}) from the user's wallet. The card itself stays valid and spendable.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={submitting}>Back</Button>
          <Button onClick={handleArchive} color="secondary" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAction === "sweep"} onClose={() => !submitting && setConfirmAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>Sweep leftover balance?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Converts the leftover pennies on card <strong>{cardRef(card)}</strong> ({card.email}) into a mortgage reward and sets the card to <strong>swept</strong>.
            <br /><br />
            No money moves — the balance stays on the card at the provider, so only sweep an amount you have confirmed is actually left.
          </DialogContentText>
          <TextField
            label="Sweep amount"
            value={sweepAmount}
            onChange={(e) => setSweepAmount(e.target.value)}
            error={!!sweepAmountError()}
            helperText={sweepAmountError() || `Must be under £${SWEEP_MAX.toFixed(2)}.`}
            type="number"
            size="small"
            style={{ marginTop: 12, width: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmAction(null); setSweepAmount(""); }} disabled={submitting}>Back</Button>
          <Button onClick={handleSweep} color="primary" variant="contained" disabled={submitting || !!sweepAmountError()}>
            {submitting ? <CircularProgress size={20} /> : "Sweep"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAction === "cancel"} onClose={() => !submitting && closeCancelDialog()} fullWidth maxWidth="sm">
        <DialogTitle>Cancel card?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sets card <strong>{cardRef(card)}</strong> ({card.email}) to <strong>cancelled</strong>, hides it from the user, and claws back any still-pending cashback reward (£{parseFloat(card.cashback_amount || 0).toFixed(2)}) from their in-flight balance. A reward already paid out is left untouched.
            <br /><br />
            Cancelling does not reclaim any balance still spendable on the card at the provider.
          </DialogContentText>

          <Box style={{ marginTop: 12, padding: 12, border: "1px solid #e0e0e0", borderRadius: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={refundFunds}
                  onChange={(e) => setRefundFunds(e.target.checked)}
                  color="primary"
                  disabled={!!card.is_refunded}
                />
              }
              label={card.is_refunded ? "Already refunded" : "Also refund the purchase"}
            />
            {card.is_refunded ? (
              <Typography variant="caption" color="textSecondary" component="div">
                This card is already marked refunded, so no further refund can be sent.
              </Typography>
            ) : (
              <>
                <TextField
                  label="Refund amount"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  disabled={!refundFunds}
                  error={!!refundAmountError()}
                  helperText={refundAmountError() || `Paid £${parseFloat(card.denomination || 0).toFixed(2)}. Lower this for a partial refund.`}
                  type="number"
                  size="small"
                  style={{ marginTop: 8, width: 220 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                />
                {refundFunds && (
                  <Typography variant="caption" color="error" component="div" style={{ marginTop: 8 }}>
                    A refund of any size marks this card refunded — a partial refund cannot be topped up later.
                  </Typography>
                )}
                {refundFunds && ["pending_payment", "payment_failed"].includes(card.status) && (
                  <Typography variant="caption" color="error" component="div" style={{ marginTop: 8 }}>
                    Status is <strong>{card.status}</strong> — the payment may never have been taken. Confirm the funds actually left the user's bank before refunding.
                  </Typography>
                )}
              </>
            )}
          </Box>

          <TextField
            label="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            multiline
            size="small"
            style={{ marginTop: 12 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={submitting}>Back</Button>
          <Button onClick={handleCancel} color="secondary" variant="contained" disabled={submitting || !!refundAmountError()}>
            {submitting ? <CircularProgress size={20} /> : refundFunds ? "Cancel & refund" : "Cancel card"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default MastercardAnywhereDetail;
